import React from 'react';
import type { SearchIndex } from 'algoliasearch';
import type { Article } from '@atlaskit/help';
import { createArticleObject, articleId as ArticleId } from '@atlaskit/help';
import { PRODUCT_NAME_ID } from '@css-xp/ppc-constants';
import { AnalyticsTrackSubject, trackLinkClick } from '@atlassian/temp-analytics';

const fixAlgoliaImagesAndLinkTags = (articleContent: any): string => {
  const htmlObject = document.createElement('div');
  htmlObject.innerHTML = articleContent;

  const imgs = htmlObject.getElementsByTagName('img');
  const links = htmlObject.getElementsByTagName('a');

  // Open images in a new tab/window
  for (let q = 0; q < imgs.length; q += 1) {
    const img = imgs[q];
    const imgParent = img.parentNode;
    const imgUrl = img.getAttribute('src');
    const wrapper = document.createElement('a');
    if (imgUrl !== null) {
      img.setAttribute('style', 'display: block; margin-top: 0; max-width: 100%;');
      wrapper.setAttribute('href', imgUrl);
      wrapper.setAttribute('target', '_blank');
      wrapper.setAttribute('style', 'display:inline-block; max-width: 100%; margin-top: 20px;');
      if (imgParent) {
        imgParent.insertBefore(wrapper, img);
        wrapper.appendChild(img);
      }
    }
  }

  // open links in a new tab/window
  for (let q = 0; q < links.length; q += 1) {
    const link = links[q];
    link.setAttribute('target', '_blank');
  }

  return htmlObject.outerHTML;
};

export const useGetHelpArticles: (
  indexInAlgolia: SearchIndex
) => {
  getRelatedArticles: (routeGroup: string, routeName: string) => Promise<any>;
  getHelpArticle: (articleId: ArticleId) => Promise<Article>;
} = (indexInAlgolia) => {
  const getRelatedArticles = React.useCallback(
    async (routeGroup: string, routeName: string): Promise<any> => {
      if (routeName) {
        const facetFilters = [
          `productNameId:${PRODUCT_NAME_ID}`,
          `routes.routeGroup:${routeGroup}`,
          `routes.routeName:${routeName}`,
          'order:1',
        ];
        const algoliaResponse = await indexInAlgolia.search('', { facetFilters });

        return algoliaResponse?.hits;
      }
    },
    [indexInAlgolia]
  );

  const getHelpArticle = React.useCallback(
    async (articleId: ArticleId): Promise<Article> => {
      trackLinkClick(AnalyticsTrackSubject.SELF_HELP, {
        articleId: articleId?.id,
      });
      const res = await indexInAlgolia.search('', {
        filters: `id:${articleId.id}`,
      });

      const articleObject = createArticleObject(res.hits[0], res.hits[1]);
      if (articleObject) {
        articleObject.body = fixAlgoliaImagesAndLinkTags(articleObject.body);
        return articleObject;
      }
      // @ts-ignore
      return null;
    },
    [indexInAlgolia]
  );

  return {
    getRelatedArticles,
    getHelpArticle,
  };
};
